import { useApolloClient } from "@apollo/client";
import {
  DataGrid,
  Modal,
  ModalOpenButton,
  useLocalStorageAsState,
  useScreenWidth,
} from "@msys/ui";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { Button, IconButton, Stack, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import * as Sentry from "@sentry/react";
import { useTranslate } from "@tolgee/react";
import { groupBy, omit } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import {
  CustomFieldObjectType,
  SetCustomFieldConfigInput,
} from "../../../clients/graphqlTypes.js";
import { FullScreenToggleIconButton } from "../../commons/button/FullScreenToggleIconButton.js";
import { ConfirmModal } from "../../commons/modals/ConfirmModal.js";
import { useDataGridStateStore } from "../users/useDataGridStateStore.js";
import {
  AddOrEditCustomFieldModal,
  FormValues,
} from "./AddOrEditCustomFieldModal.js";
import { useSetCustomFieldConfigMutation } from "./EditCustomFieldConfigModal.generated.js";
import {
  CustomFieldConfigFragment,
  useCustomFieldConfigQuery,
} from "./customFieldConfigs.generated.js";
import { useCustomFieldDataTypes } from "./useCustomFieldDataTypes.js";
import { useCustomFieldObjectTypes } from "./useCustomFieldObjectTypes.js";

export const EditCustomFieldConfigModal = ({
  handleClose,
  refetchQueries,
}: {
  handleClose: () => void;
  refetchQueries: string[];
}) => {
  const { t } = useTranslate(["CustomFields", "Global"]);
  const { isMaxPhone } = useScreenWidth();

  const client = useApolloClient();
  const customFieldConfigQuery = useCustomFieldConfigQuery({ client });
  const { customFieldObjectTypeLabels } = useCustomFieldObjectTypes();

  const customFieldConfig = React.useMemo(
    () => customFieldConfigQuery.data?.customFieldConfig ?? [],
    [customFieldConfigQuery.data]
  );

  const { addConfig, changeConfig, removeConfig } = useCustomFieldsActions(
    customFieldConfig,
    refetchQueries
  );

  const [isFullScreen, setIsFullScreen] = useLocalStorageAsState<boolean>(
    "msys-custom-fields-modal-fullscreen",
    false,
    true
  );

  const groups = React.useMemo(
    () => groupBy(customFieldConfig, c => c.objectType),
    [customFieldConfig]
  );

  return (
    <Modal
      title={t("Custom fields configurator", {
        ns: "CustomFields",
      })}
      handleClose={handleClose}
      maxWidth="xl"
      dialogProps={
        !isMaxPhone && isFullScreen ? { fullScreen: true } : undefined
      }
      headerActions={
        !isMaxPhone ? (
          <FullScreenToggleIconButton
            isFullScreen={isFullScreen}
            setIsFullScreen={setIsFullScreen}
          />
        ) : undefined
      }
    >
      <Stack direction={"column"} spacing={2}>
        <Stack direction={"row"} justifyContent="flex-end" spacing={1}>
          <ModalOpenButton
            Modal={AddOrEditCustomFieldModal}
            modalProps={{
              config: null,
              async handleComplete(values, handleClose) {
                await addConfig(values);
                handleClose();
              },
            }}
          >
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              size="small"
              disableElevation
            >
              {t("New custom field", { ns: "CustomFields" })}
            </Button>
          </ModalOpenButton>
        </Stack>
        {Object.entries(groups).map(([objectType, configs]) => (
          <Stack direction="column" spacing={1} key={`group-${objectType}`}>
            <Typography variant="h3">
              {customFieldObjectTypeLabels[objectType as CustomFieldObjectType]}
            </Typography>
            <CustomFieldsConfigTable
              configs={configs}
              loading={customFieldConfigQuery.loading}
              onRemove={removeConfig}
              onChange={changeConfig}
            />
          </Stack>
        ))}
      </Stack>
    </Modal>
  );
};

function getConfigId(config: CustomFieldConfigFragment) {
  return `${config.key}-${config.objectType}-${config.dataType}`;
}

function valuesToInput(values: FormValues): SetCustomFieldConfigInput {
  return {
    ...values,
    allowedValues:
      values.dataType !== "t_text"
        ? ""
        : values.allowedValues
            .map(v => v.trim())
            .filter(Boolean)
            .join("\n")
            .toLocaleLowerCase(),
  };
}
function configToInput(
  config: CustomFieldConfigFragment
): SetCustomFieldConfigInput {
  return omit(config, ["id", "__typename"]);
}

function useCustomFieldsActions(
  existingConfigs: CustomFieldConfigFragment[],
  refetchQueries?: string[]
) {
  const { t } = useTranslate(["CustomFields", "Global"]);
  const { enqueueSnackbar } = useSnackbar();

  const client = useApolloClient();
  const [setCustomFieldConfig] = useSetCustomFieldConfigMutation({
    client,
    awaitRefetchQueries: true,
    refetchQueries,
  });

  const addConfig = async (values: FormValues) => {
    try {
      await setCustomFieldConfig({
        variables: {
          input: [...existingConfigs.map(configToInput), valuesToInput(values)],
        },
      });
      enqueueSnackbar(
        t("Custom field config set", {
          ns: "CustomFields",
        })
      );
    } catch (e) {
      Sentry.captureException(e);
      enqueueSnackbar(
        t("Failed to set custom field config", { ns: "CustomFields" }),
        { variant: "error" }
      );
    }
  };
  const changeConfig = async (
    config: CustomFieldConfigFragment,
    values: FormValues
  ) => {
    try {
      await setCustomFieldConfig({
        variables: {
          input: existingConfigs.map(c =>
            getConfigId(c) === getConfigId(config)
              ? valuesToInput(values)
              : configToInput(c)
          ),
        },
      });
      enqueueSnackbar(
        t("Custom field config set", {
          ns: "CustomFields",
        })
      );
    } catch (e) {
      Sentry.captureException(e);
      enqueueSnackbar(
        t("Failed to set custom field config", { ns: "CustomFields" }),
        { variant: "error" }
      );
    }
  };
  const removeConfig = async (config: CustomFieldConfigFragment) => {
    try {
      await setCustomFieldConfig({
        variables: {
          input: existingConfigs
            .filter(c => getConfigId(c) !== getConfigId(config))
            .map(configToInput),
        },
      });
      enqueueSnackbar(
        t("Custom field config set", {
          ns: "CustomFields",
        })
      );
    } catch (e) {
      Sentry.captureException(e);
      enqueueSnackbar(
        t("Failed to set custom field config", { ns: "CustomFields" }),
        { variant: "error" }
      );
    }
  };

  return { addConfig, changeConfig, removeConfig };
}

function CustomFieldsConfigTable({
  configs,
  onRemove,
  onChange,
  loading,
}: {
  configs: CustomFieldConfigFragment[];
  onRemove: (config: CustomFieldConfigFragment) => Promise<void> | void;
  onChange: (
    config: CustomFieldConfigFragment,
    values: FormValues
  ) => Promise<void> | void;
  loading?: boolean;
}) {
  const { t } = useTranslate(["CustomFields", "Global"]);
  const { customFieldObjectTypeLabels } = useCustomFieldObjectTypes();
  const { customFieldDataTypeLabels } = useCustomFieldDataTypes();

  const refs = React.useRef({ onRemove, onChange });
  refs.current = { onRemove, onChange };

  const stateStore = useDataGridStateStore("CustomFieldsConfigTable");

  const columns = React.useMemo(
    () =>
      [
        {
          field: "objectType",
          headerName: t("Component", { ns: "CustomFields" }),
          sortable: false,
          groupable: false,
          flex: 1,
          renderCell: ({ row }) => customFieldObjectTypeLabels[row.objectType],
        },
        {
          field: "key",
          headerName: t("Field key", { ns: "CustomFields" }),
          sortable: false,
          groupable: false,
          flex: 1,
        },
        {
          field: "label",
          headerName: t("Field label", { ns: "CustomFields" }),
          sortable: false,
          groupable: false,
          flex: 1,
        },
        {
          field: "dataType",
          headerName: t("Field type", { ns: "CustomFields" }),
          sortable: false,
          groupable: false,
          flex: 1,
          renderCell: ({ row }) => customFieldDataTypeLabels[row.dataType],
        },
        {
          field: "allowedValues",
          headerName: t("Picklist options", { ns: "CustomFields" }),
          sortable: false,
          groupable: false,
          flex: 1,
          renderCell: ({ row }) => {
            const options =
              row.dataType === "t_text"
                ? row.allowedValues.split("\n").filter(Boolean)
                : [];
            return !options.length ? "–" : options.join(", ");
          },
        },
        {
          field: "group",
          headerName: t("Category", { ns: "CustomFields" }),
          sortable: false,
          groupable: false,
          flex: 1,
        },
        {
          field: "showAsColumnInList",
          headerName: t("Show as column", { ns: "CustomFields" }),
          sortable: false,
          groupable: false,
          flex: 0.5,
          renderCell: ({ row }) =>
            row.showAsColumnInList
              ? t("Yes", { ns: "Global" })
              : t("No", { ns: "Global" }),
        },
        {
          field: "showInCreateForm",
          headerName: t("Show in create form", { ns: "CustomFields" }),
          sortable: false,
          groupable: false,
          flex: 0.5,
          renderCell: ({ row }) =>
            row.showInCreateForm
              ? t("Yes", { ns: "Global" })
              : t("No", { ns: "Global" }),
        },
        {
          field: "showValueAsChip",
          headerName: t("Show value as chip", { ns: "CustomFields" }),
          sortable: false,
          groupable: false,
          flex: 0.5,
          renderCell: ({ row }) =>
            row.showValueAsChip
              ? t("Yes", { ns: "Global" })
              : t("No", { ns: "Global" }),
        },
        {
          field: "edit",
          headerName: "",
          sortable: false,
          groupable: false,
          flex: 0,
          minWidth: 34,
          maxWidth: 34,
          renderCell({ row }) {
            return (
              <ModalOpenButton
                Modal={AddOrEditCustomFieldModal}
                modalProps={{
                  config: row,
                  async handleComplete(values, handleClose) {
                    await refs.current.onChange(row, values);
                    handleClose();
                  },
                }}
              >
                <IconButton
                  color="primary"
                  size={"small"}
                  sx={{ marginLeft: "-10px" }}
                >
                  <EditIcon />
                </IconButton>
              </ModalOpenButton>
            );
          },
        },
        {
          field: "delete",
          headerName: "",
          sortable: false,
          groupable: false,
          flex: 0,
          minWidth: 34,
          maxWidth: 34,
          renderCell({ row }) {
            return (
              <ModalOpenButton
                Modal={ConfirmModal}
                modalProps={{
                  title: t(
                    "Are you sure you want to remove this custom field config?",
                    { ns: "CustomFields" }
                  ),
                  async handleConfirm() {
                    await refs.current.onRemove(row);
                  },
                }}
              >
                <IconButton
                  color="primary"
                  size={"small"}
                  sx={{ marginLeft: "-10px" }}
                >
                  <DeleteIcon />
                </IconButton>
              </ModalOpenButton>
            );
          },
        },
      ] as GridColDef<CustomFieldConfigFragment>[],
    [customFieldDataTypeLabels, customFieldObjectTypeLabels, t]
  );

  return (
    <DataGrid<CustomFieldConfigFragment>
      loading={loading}
      stateStore={stateStore}
      columns={columns}
      // disableColumnMenu
      disableVirtualization
      rows={configs}
      getRowId={getConfigId}
      paginationMode="client"
      sortingMode="client"
      disableRowSelectionOnClick
    />
  );
}
